/**
 * routingDraft — pure helpers for the brain's Routing facet (ADR-111 IA-5).
 * `agents/<brain>/skills/fleet-roster/SKILL.md` has a GENERATED region (the
 * delegation matrix + specialist capabilities, rendered by
 * scripts/gen-fleet-roster.py from each specialist's `routing:` in
 * config/fleet.yaml, ADR-113) fenced between BEGIN/END markers, and
 * hand-authored prose outside it (frontmatter, the H1, "When to use",
 * "Pitfalls", disambiguation notes). The curated-overlay editor may only touch
 * the prose OUTSIDE the fence — these helpers split it out for editing and
 * reassemble it before proposing, so the generated block is never hand-edited
 * out of sync with fleet.yaml. Mirrors scripts/gen-fleet-roster.py's own
 * BEGIN/END markers verbatim. DOM-free and unit-tested.
 */

// Must match scripts/gen-fleet-roster.py's BEGIN/END exactly.
const BEGIN_RE = /<!-- BEGIN generated: fleet-roster[\s\S]*?-->/
const END_MARKER = '<!-- END generated: fleet-roster -->'

export interface RosterSplit {
  /** Everything before the generated block (frontmatter, H1, lead-in prose). */
  before: string
  /** The generated block itself, BEGIN…END inclusive — read-only, verbatim. */
  fenced: string
  /** Everything after the generated block (curated notes, disambiguation). */
  after: string
  /** False when no BEGIN/END fence was found (not yet generated, or malformed) —
   *  `before` then holds the whole raw file and `fenced`/`after` are empty. */
  hasFence: boolean
}

/** Split a fleet-roster SKILL.md into its editable prose and its generated block. */
export function splitRoster(raw: string): RosterSplit {
  const beginMatch = BEGIN_RE.exec(raw)
  if (!beginMatch) return { before: raw, fenced: '', after: '', hasFence: false }
  const beginIdx = beginMatch.index
  const endIdx = raw.indexOf(END_MARKER, beginIdx)
  if (endIdx === -1) return { before: raw, fenced: '', after: '', hasFence: false }
  const endOfEnd = endIdx + END_MARKER.length
  return {
    before: raw.slice(0, beginIdx),
    fenced: raw.slice(beginIdx, endOfEnd),
    after: raw.slice(endOfEnd),
    hasFence: true,
  }
}

/** Reassemble an edited `before`/`after` around the ORIGINAL (untouched) fenced
 *  block. When the source had no fence, there's nothing generated to preserve —
 *  `newBefore` is the whole edited document. */
export function reassembleRoster(split: RosterSplit, newBefore: string, newAfter: string): string {
  if (!split.hasFence) return newBefore
  return newBefore + split.fenced + newAfter
}
